import { initializeApp, getApps } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, signInWithCustomToken,
  createUserWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, updateProfile, signOut, onAuthStateChanged,
} from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, onSnapshot, collection, query, orderBy, limit, getDocs, serverTimestamp, arrayUnion } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const APP_CHECK_SITE_KEY = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY; // public reCAPTCHA Enterprise site key
const REGION = "australia-southeast1";
const SITE_ID = "roan";

export const firebaseEnabled = Boolean(config.apiKey && config.projectId);

let appCheckInit = false;
const app = () => {
  const a = getApps()[0] || initializeApp(config);
  if (APP_CHECK_SITE_KEY && !appCheckInit) {
    appCheckInit = true;
    try {
      initializeAppCheck(a, { provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY), isTokenAutoRefreshEnabled: true });
    } catch (e) {
      console.error("App Check init failed", e);
    }
  }
  return a;
};
export const fbAuth = () => getAuth(app());
const fbDb = () => getFirestore(app());
const fbFunctions = () => getFunctions(app(), REGION);

export const fbGoogle = () => signInWithPopup(fbAuth(), new GoogleAuthProvider());
export const fbSignIn = (email, password) => signInWithEmailAndPassword(fbAuth(), email, password);
export const fbSignUp = async (email, password, name) => {
  const cred = await createUserWithEmailAndPassword(fbAuth(), email, password);
  if (name) await updateProfile(cred.user, { displayName: name });
  return cred;
};
export const fbReset = (email) => sendPasswordResetEmail(fbAuth(), email);
export const fbVerify = (user) => sendEmailVerification(user);
export const fbSignOut = () => fbAuth().signOut?.() ?? signOut(fbAuth());
export const onFbUser = (cb) => onAuthStateChanged(fbAuth(), cb);
export const fbIdToken = async (force = false) => {
  const u = fbAuth().currentUser;
  return u ? u.getIdToken(force) : null;
};

// ---- Average AF hub (account.averageaf.com) — one-time login code → custom token ----
export const AAF_HUB_LOGIN = "https://account.averageaf.com/login";
export const AAF_CODE_PARAM = "aaf_code";
export const AAF_EXPIRED_MSG = "That sign-in link has expired. Choose Continue with Average AF again.";
export const aafLoginUrl = () => {
  const here = new URL(window.location.href);
  here.searchParams.delete(AAF_CODE_PARAM);
  return `${AAF_HUB_LOGIN}?site=${SITE_ID}&return=${encodeURIComponent(here.toString())}`;
};
export const exchangeLoginCode = async (code) => {
  const { data } = await httpsCallable(fbFunctions(), "exchangeLoginCode")({ code, siteId: SITE_ID });
  const token = data?.token || data?.customToken;
  if (!token) throw new Error(`exchangeLoginCode returned no token: ${JSON.stringify(data)}`);
  const cred = await signInWithCustomToken(fbAuth(), token);
  return cred.user;
};

// ---- Shared profile (written by the signed-in person; rules block email/createdAt/createdOnSite/status) ----
const splitName = (name = "") => {
  const [first, ...rest] = name.trim().split(" ");
  return { firstName: first || "", lastName: rest.join(" ") };
};

export const syncRoanProfile = async (user, member) => {
  if (!user || !member) return;
  const db = fbDb();
  const core = doc(db, "users", user.uid);
  const site = doc(db, "users", user.uid, "sites", SITE_ID);
  const now = serverTimestamp();
  await setDoc(core, { ...splitName(member.name), displayName: member.name, photoURL: user.photoURL || "", sitesJoined: arrayUnion(SITE_ID), updatedAt: now }, { merge: true });
  const existing = await getDoc(site);
  await setDoc(site, {
    postcode: member.postcode, state: member.state || null, federalDivision: member.electorate, fermInterest: true, memberNumber: member.member_number || null,
    groupNumber: member.group_number ?? null, roanMemberId: member.id, profileComplete: true, updatedAt: now, ...(existing.exists() ? {} : { joinedAt: now }),
  }, { merge: true });
};

export const readAccountStatus = async (uid) => {
  const snap = await getDoc(doc(fbDb(), "users", uid));
  return snap.exists() ? snap.data().status || "active" : "active";
};

// ---- WOW Points (owner reads points/{uid} and its ledger) ----
export const watchPoints = (uid, cb) =>
  onSnapshot(doc(fbDb(), "points", uid), (snap) => {
    const d = snap.exists() ? snap.data() : {};
    cb({ balance: Number(d.balance || 0), lifetime: Number(d.lifetimeEarned || 0), exists: snap.exists() });
  }, () => cb({ balance: 0, lifetime: 0, exists: false, error: true }));

export const fetchLedger = async (uid) => {
  const q = query(collection(fbDb(), "points", uid, "ledger"), orderBy("createdAt", "desc"), limit(10));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const e = d.data();
    const ts = e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt ? new Date(e.createdAt) : null;
    return { id: d.id, type: e.type, amount: Number(e.amount || 0), site_id: e.siteId, reason: e.reason, note: e.note || "", created_at: ts ? ts.toISOString() : null };
  });
};

// ---- Shared admin callable (only UIDs on the shared project's admin list may call it) ----
export const setAccountStatus = (uid, status, reason) => httpsCallable(fbFunctions(), "setAccountStatus")({ uid, status, reason, siteId: SITE_ID });

export const fbError = (e) => ({
  "auth/invalid-credential": "Email or password is incorrect.",
  "auth/wrong-password": "Email or password is incorrect.",
  "auth/user-not-found": "No Average AF account with that email yet — choose “Create account”.",
  "auth/email-already-in-use": "An Average AF account already exists for that email — sign in instead.",
  "auth/weak-password": "Password needs at least 6 characters.",
  "auth/popup-closed-by-user": "Google sign-in was closed before finishing.",
  "auth/popup-blocked": "Your browser blocked the Google pop-up. Allow pop-ups and try again.",
  "auth/unauthorized-domain": "This site isn't authorised in Firebase yet (add the domain under Authentication → Settings).",
  "auth/user-disabled": "This Average AF account has been suspended.",
  "functions/permission-denied": "Your Average AF account isn't on the shared admin list, so it can't change account status.",
  "functions/unauthenticated": "Sign in with your Average AF admin account first.",
}[e?.code] || e?.message || "Sign-in failed");
